import { useEffect } from "react";

/**
 * Injects a `<script type="application/ld+json">` block into <head> and cleans up
 * when the component unmounts. Each block is keyed by `id` so navigating between
 * pages replaces (not stacks) the schema.
 */
export default function JsonLd({ id, schema }) {
  useEffect(() => {
    if (typeof document === "undefined" || !schema) return;
    const scriptId = `jsonld-${id}`;
    // Remove any prior instance
    const prev = document.getElementById(scriptId);
    if (prev) prev.remove();
    const s = document.createElement("script");
    s.id = scriptId;
    s.type = "application/ld+json";
    s.text = JSON.stringify(schema);
    document.head.appendChild(s);
    return () => {
      const el = document.getElementById(scriptId);
      if (el) el.remove();
    };
  }, [id, schema]);
  return null;
}

export function orgSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "ClickForge Digital Solutions",
    url: "https://clickforgeds.com",
    logo: "https://clickforgeds.com/logo.png",
    email: "info@clickforgeds.com",
    description:
      "Real Midwest business lead generation, AI audits, and automated outreach for solo agencies and closers.",
    areaServed: {
      "@type": "AdministrativeArea",
      name: "Midwestern United States",
    },
    sameAs: [],
  };
}

export function productSchema(tiers) {
  // Minimum viable Product + Offer schema so Google can surface pricing
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "ClickForge Lead Agent",
    description:
      "Multi-tenant lead generation agent: real OSM business discovery, Hunter.io enrichment, AI audits and cold-email outreach via Resend, Stripe proposal checkout, CRM pipeline.",
    brand: { "@type": "Brand", name: "ClickForge Digital Solutions" },
    offers: (tiers || []).map((t) => ({
      "@type": "Offer",
      name: `${t.name} plan`,
      price: String(t.amount_usd || 0),
      priceCurrency: "USD",
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price: String(t.amount_usd || 0),
        priceCurrency: "USD",
        billingIncrement: 1,
        unitText: "MONTH",
      },
      availability: "https://schema.org/InStock",
      url: "https://clickforgeds.com/pricing",
    })),
  };
}

export function faqSchema(qas) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: (qas || []).map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };
}
